import { useEffect, useState, useMemo, useCallback } from "react";
import iconRenewel2 from "../../../assets/media/icon-renewel-2.svg";
import iconSearch from "../../../assets/media/icon-search.svg";
import iconSwap from "../../../assets/media/icon-swap.svg";
import iconTick from "../../../assets/media/icon-tick.svg";
import iconTimeSquare from "../../../assets/media/icon-time-square.svg";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchApplications } from "../../../store/reducers/applicationSlice";
import { setActiveItem } from "../../../store/reducers/counterSlice";
import { handleFilesDownload } from "../../../store/reducers/fileSlice";
import Header from "../../header";
import Navbar from "../../navbar";
import { formatDate } from "../applications";
import Pagination from "./appPagination";
import NotFound from "../../common/notFound";

// Import React Table
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

// Import Bootstrap JavaScript
import "bootstrap/dist/js/bootstrap.bundle.min";

function CheckboxFilter({
  checkboxes,
  setCheckboxes,
  setSearchTerm,
  visibleCheckboxes,
}) {
  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    if (value === "All") {
      setCheckboxes((prevCheckboxes) => {
        const newCheckboxes = { ...prevCheckboxes };
        for (const key in newCheckboxes) {
          newCheckboxes[key] = checked;
        }
        return newCheckboxes;
      });
      setSearchTerm("");
    } else {
      setCheckboxes((prevCheckboxes) => ({
        ...prevCheckboxes,
        [value]: checked,
      }));
      setSearchTerm("");
    }
  };

  // Filter checkboxes based on visibleCheckboxes prop
  const checkboxKeys = visibleCheckboxes
    ? Object.keys(checkboxes).filter((key) => visibleCheckboxes.includes(key))
    : Object.keys(checkboxes);

  return (
    <div className="boardFilter">
      {checkboxKeys?.map((checkboxValue) => (
        <div className="form-check form-check-inline" key={checkboxValue}>
          <input
            id={`checkbox-${checkboxValue}`}
            className="form-check-input"
            type="checkbox"
            value={checkboxValue}
            checked={checkboxes[checkboxValue]}
            onChange={handleCheckboxChange}
          />
          <label
            className="form-check-label"
            htmlFor={`checkbox-${checkboxValue}`}
          >
            {checkboxValue}
          </label>
        </div>
      ))}
    </div>
  );
}

const stateButtonMap = {
  in_process: {
    className: "status-inprocess",
    text: "In Process",
    state: "in_process",
  },

  pending: { className: "status-pending", text: "Pending", state: "pending" },
  approved: {
    className: "status-approved",
    text: "Approved",
    state: "approved",
  },
  rejected: {
    className: "status-rejected",
    text: "Rejected",
    state: "rejected",
  },
  discrepancies: {
    className: "status-discrepancies",
    text: "Discrepancies",
    state: "discrepancies",
  },
  pending_for_approval: {
    className: "status-pending-for-approval",
    text: "Pending For Approval",
    state: "pending_for_approval",
  },
  drafts: { className: "status-drafts", text: "Drafts", state: "drafts" },
  renewal: { className: "status-renewal", text: "Renewal", state: "renewal" },
  // The 'in_review' button should match the 'state' property in your app data.
  // Check if your app data uses one of these potential values for "In Review":
  // - "in_review"
  // - "in-review"
  // - "application_submitted_by_company_user_to_company"
  // For compatibility, support both:
  in_review: {
    className: "status-in-review",
    text: "In Review",
    state: [
      "in_review",
      "in-review",
      "application_submitted_by_company_user_to_company",
    ],
  },
};

// Helper function to find stateInfo by checking state value (handles arrays)
function findStateInfo(state) {
  if (!state) return null;

  // First try direct lookup
  if (stateButtonMap[state]) {
    return stateButtonMap[state];
  }

  // Then check if state matches any array values
  for (const [, value] of Object.entries(stateButtonMap)) {
    if (Array.isArray(value.state)) {
      if (value.state.includes(state)) {
        return value;
      }
    } else if (value.state === state) {
      return value;
    }
  }

  return null;
}

function countStateOccurrences(apps) {
  const stateCounts = {};
  apps?.forEach((app) => {
    const state = app?.state;
    if (!state) return;

    // Find which stateButtonMap entry this state belongs to
    let mappedState = state;
    for (const [key, value] of Object.entries(stateButtonMap)) {
      if (Array.isArray(value.state)) {
        if (value.state.includes(state)) {
          mappedState = key;
          break;
        }
      } else if (value.state === state) {
        mappedState = key;
        break;
      }
    }

    if (stateCounts[mappedState]) {
      stateCounts[mappedState]++;
    } else {
      stateCounts[mappedState] = 1;
    }
  });
  return stateCounts;
}

const AppDashboard = () => {
  // Define which checkboxes should be visible
  // Set to null or undefined to show all checkboxes
  // Example: ["All", "In Process", "Pending", "Approved", "In Review"]
  const visibleCheckboxes = [
    "All",
    "In Process",
    "In Review",
    "Pending",
    "Approved",
    "Rejected",
  ]; // Change this to limit visible checkboxes

  const initialState = {
    All: true,
    "In Process": false,
    Pending: false,
    Rejected: false,
    Approved: false,
    Discrepancies: false,
    "Pending For Approval": false,
    Drafts: false,
    Renewal: false,
    "In Review": false,
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [checkboxes, setCheckboxes] = useState(initialState);
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);

  const [startDate, endDate] = dateRange;

  const applications = countStateOccurrences(
    useSelector((state) => state.applications.data)
  );
  const applicationRecords = useSelector((state) => state.applications.data);
  console.log("🚀 ~ AppDashboard ~ applicationRecords:", applicationRecords);

  // Debug: Check what states are in the data
  const statesInData = [
    ...new Set(applicationRecords?.map((app) => app.state) || []),
  ];
  console.log("States in data:", statesInData);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth.user.access_token);

  // filter function
  const filtered = (search = "") => {
    // SECURITY: Filter by company_id and user role to prevent cross-company access
    const userRole = user?.user?.user_role;
    const userCompanyId = user?.user?.company_id;
    const currentUserId = user?.user?.id;

    // First, filter by company and role
    const minifiedData = (applicationRecords || []).filter((record) => {
      if (!record) return false;

      const recordCompanyId = record.company_id || record.companyId;
      const recordUserId = record.user_id || record.userId || record.user?.id;

      // Company admin: can see all applications in their company
      if (userRole === "company_admin") {
        return String(recordCompanyId) === String(userCompanyId);
      }
      // Employee: can only see their own applications
      else if (userRole === "employee") {
        return (
          String(recordUserId) === String(currentUserId) &&
          String(recordCompanyId) === String(userCompanyId)
        );
      }
      // Default: filter by company (fallback for other roles)
      else {
        return String(recordCompanyId) === String(userCompanyId);
      }
    });

    const filteredData = minifiedData.map((item) => {
      const stateInfo = findStateInfo(item?.state);
      if (!stateInfo) return undefined; // safeguard unknown/missing states

      // Check if state matches (handles both string and array cases)
      const stateMatches = Array.isArray(stateInfo.state)
        ? stateInfo.state.includes(item.state)
        : stateInfo.state === item.state;

      const isStateChecked =
        (stateMatches && checkboxes[stateInfo.text]) ||
        checkboxes["All"] === true;

      if (!isStateChecked) return undefined;

      return {
        ...item,
        created_at: formatDate(item.created_at),
        updated_at: formatDate(item.updated_at),
        stateText: stateInfo.text,
      };
    });

    const filterUndef = filteredData.filter(Boolean);

    const finalArray = filterUndef.filter((item) => {
      for (const key in item) {
        if (
          typeof item[key] === "string" &&
          item[key].toLowerCase().includes(search.toLowerCase())
        ) {
          return true;
        }
      }
      return false;
    });

    console.log("Filtered data:", {
      originalRecords: applicationRecords?.length,
      minifiedData: minifiedData.length,
      filteredData: filterUndef.length,
      finalArray: finalArray.length,
    });
    setData(finalArray);
    return finalArray;
  };

  // debounced search
  useEffect(() => {
    const performSearch = async () => {
      const dataSearch = await filtered(searchTerm);
      setData(dataSearch);
      setCurrentPage(1); // Reset to first page when search changes
    };
    const debounceTimer = setTimeout(() => {
      performSearch();
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * useEffect to update filtered data whenever the application records or checkbox filters change.
   */
  useEffect(() => {
    setData(filtered());
    setCurrentPage(1); // Reset to first page when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationRecords, checkboxes]);

  useEffect(() => {
    let queryParams = {};

    // SECURITY: Add company_id filter at API level for company_admin
    if (user?.user?.user_role === "company_admin" && user?.user?.company_id) {
      queryParams.company_id = user.user.company_id;
    }
    // SECURITY: Add user_id filter at API level for employees
    else if (user?.user?.user_role === "employee" && user?.user?.id) {
      queryParams.user_id = user.user.id;
    }

    if (startDate && endDate) {
      queryParams.start_date = startDate.toISOString();
      queryParams.end_date = endDate.toISOString();
    }
    dispatch(
      fetchApplications({
        accessToken: accessToken,
        queryParams: queryParams,
        fetchAllPages: true, // Fetch all pages to show all applications
      })
    );
  }, [
    accessToken,
    dispatch,
    startDate,
    endDate,
    user?.user?.user_role,
    user?.user?.company_id,
    user?.user?.id,
  ]);

  const applicationLoading = useSelector((state) => state.applications.loading);
  const userLoading = useSelector((state) => state.auth.loading);

  const handleDownload = useCallback(
    async (item) => {
      try {
        await dispatch(
          handleFilesDownload({
            userId: item.user_id,
            applicationId: item.id,
            authToken: accessToken,
          })
        );
      } catch (error) {
        console.error("Download failed", error);
      }
    },
    [dispatch, accessToken]
  );

  const handleDelete = useCallback((item) => {
    // Implement your delete logic here
    console.log("Delete item:", item);
    // You can dispatch an action to delete the application
  }, []);

  // Define columns for React Table
  const columns = useMemo(
    () => [
      {
        id: "serial_number",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      },
      {
        id: "application_number",
        header: "Application No.",
        cell: ({ row }) => `GB-${row.original.id}`,
      },
      {
        id: "application_name",
        header: "Application Name",
        cell: ({ row }) => (
          <div className="boardTitle">
            <strong>{row.original.name}</strong>
            <span>{row.original.categoryName}</span>
          </div>
        ),
      },
      {
        id: "submitted_by",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;

          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.email || 'N/A'}</span>
            </div>
          );
        },
      },
      {
        id: "submitted_for",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="">
              <strong>{row.userName}</strong>
              <span>{row.userEmail}</span>
            </div>
          );
        },
      },
      {
        id: "submitted_date",
        header: "Submitted Date",
        accessorKey: "created_at",
        cell: (info) => (
          <span className="dateText">{info.getValue()}</span>
        ),
      },
      {
        id: "registered_date",
        header: "Registered Date",
        accessorKey: "updated_at",
        cell: (info) => (
          <span className="dateText">{info.getValue()}</span>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          const stateInfo = findStateInfo(row.original.state);
          return (
            <span className={`boardButton ${stateInfo?.className || ""}`}>
              {row.original.stateText || stateInfo?.text || "Unknown"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="boardActions">
            <div className="dropdown d-inline-flex">
              <button
                className="application-dropdown dropdown-toggle border-0 bg-transparent"
                type="button"
                id={`dropdownMenuButton-${row.original.id}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <img src={iconMenuDots} alt="Menu Dots" />
              </button>

              <ul
                className="dropdown-menu"
                aria-labelledby={`dropdownMenuButton-${row.original.id}`}
              >
                <li>
                  <Link
                    to={`/view-applications/${row.original.id}`}
                    className="dropdown-item"
                  >
                    View
                  </Link>
                </li>
                <li>
                  <button
                    onClick={() => handleDelete(row.original)}
                    className="dropdown-item"
                  >
                    Delete
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleDownload(row.original)}
                    className="dropdown-item"
                  >
                    Download
                  </button>
                </li>
              </ul>
            </div>
          </div>
        ),
      },
    ],
    [currentPage, itemsPerPage, handleDownload, handleDelete, getallUserdata]
  );

  // Calculate paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data?.slice(startIndex, endIndex) || [];
  }, [data, currentPage, itemsPerPage]);

  // Create the table instance with pagination
  const table = useReactTable({
    data: paginatedData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination: {
        pageIndex: currentPage - 1, // React Table uses 0-based indexing
        pageSize: itemsPerPage,
      },
    },
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater({
          pageIndex: currentPage - 1,
          pageSize: itemsPerPage,
        });
        setCurrentPage(newState.pageIndex + 1); // Convert back to 1-based indexing
      } else {
        setCurrentPage(updater.pageIndex + 1); // Convert back to 1-based indexing
      }
    },
    manualPagination: true, // We'll handle pagination manually
  });

  if (applicationLoading || userLoading) {
    return <p>loading</p>;
  }

  const getTotalPages = () => {
    if (!data?.length) return 1;
    const total = Math.ceil(data.length / itemsPerPage);
    console.log("Total pages calculation:", {
      dataLength: data.length,
      itemsPerPage,
      totalPages: total,
    });
    return total;
  };

  return (
    <div>
      <Navbar />
      <Header />
      <div className="wrapper ">
        <div className="row01 ">
          <div className="boxApp w-100">
            <h2>My Applications</h2>
            <div className="d-flex mb-4">
              <div className="appState stateProcess w-100">
                <h3>
                  <img src={iconRenewel2} alt="" />
                  <span>{applications?.in_process || 0}</span>
                </h3>
                <h4>In Process</h4>
                <Link to={"/in-process-applications"}>View All</Link>
              </div>
              <div className="appState stateInReview w-100">
                <h3>
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      fill="red"
                      version="1.1"
                      id="Capa_1"
                      width="32px"
                      height="32px"
                      viewBox="0 0 42.574 42.574"
                      xmlSpace="preserve"
                    >
                      <g>
                        <path d="M42.449,35.771c0,2.209-1.791,4-4,4H19.158c-2.209,0-4-1.791-4-4s1.791-4,4-4h19.291   C40.658,31.771,42.449,33.562,42.449,35.771z M9.481,28.969H4.375c-2.347,0-4.25,1.901-4.25,4.25v5.105   c0,2.348,1.903,4.25,4.25,4.25h5.106c2.347,0,4.25-1.902,4.25-4.25v-5.105C13.731,30.87,11.829,28.969,9.481,28.969z    M38.449,17.287H19.158c-2.209,0-4,1.791-4,4s1.791,4,4,4h19.291c2.209,0,4-1.791,4-4S40.658,17.287,38.449,17.287z M13.731,18.734   v5.104c0,2.35-1.903,4.25-4.25,4.25H4.375c-2.347,0-4.25-1.9-4.25-4.25v-5.104c0-2.348,1.903-4.25,4.25-4.25h5.106   C11.829,14.484,13.731,16.387,13.731,18.734z M11.729,18.404c-0.586-0.586-1.536-0.586-2.121,0l-0.33,0.33l-3.253,3.254l-1.65-1.65   l-0.127-0.127c-0.586-0.586-1.535-0.586-2.121,0s-0.586,1.535,0,2.121l2.838,2.838c0.281,0.281,0.663,0.439,1.061,0.439   c0.398,0,0.779-0.158,1.061-0.439l1.33-1.33l1.066-1.066l2.248-2.246C12.315,19.939,12.315,18.99,11.729,18.404z M19.158,10.803   h19.291c2.209,0,4-1.791,4-4s-1.791-4-4-4H19.158c-2.209,0-4,1.791-4,4S16.949,10.803,19.158,10.803z M9.481,0H4.375   c-2.347,0-4.25,1.902-4.25,4.25v5.105c0,2.349,1.903,4.25,4.25,4.25h5.106c2.347,0,4.25-1.901,4.25-4.25V4.25   C13.731,1.902,11.829,0,9.481,0z" />
                      </g>
                    </svg>
                  </span>
                </h3>
                <h4>In Review</h4>
                <Link to={"/in-review"}>View All</Link>
              </div>
              <div className="appState statePending w-100">
                <h3>
                  <img src={iconTimeSquare} alt="" />
                  <span>{applications?.pending || 0}</span>
                </h3>
                <h4>Pending</h4>
                <Link to={"/pending-applications"}>View All</Link>
              </div>
              <div className="appState stateDiscrepancies w-100">
                <h3>
                  <img src={iconSwap} alt="" />
                  <span>{applications?.discrepancies || 0}</span>
                </h3>
                <h4>Discrepancies</h4>
                <Link to={"/discrepancies-company"}>View All</Link>
              </div>
              <div className="appState stateApproved w-100">
                <h3>
                  <img src={iconTick} alt="" />
                  <span>{applications?.approved || 0}</span>
                </h3>
                <h4>Approved</h4>
                <Link to={"/approved-applications"}>View All</Link>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="boxAppBoard boxWhite">
          <div
            style={{ flexWrap: "wrap", gap: "10px" }}
            className="boardHeader"
          >
            <CheckboxFilter
              setSearchTerm={setSearchTerm}
              checkboxes={checkboxes}
              setCheckboxes={setCheckboxes}
              visibleCheckboxes={visibleCheckboxes}
            />
            <div
              style={{
                gap: "10px",
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
              className="boardHeadRight"
            >
              <div className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  value={searchTerm}
                />
                <button>
                  <img src={iconSearch} alt="" />
                </button>
              </div>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setDateRange(update)}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy/MM/dd"
                />
              </div>
            </div>
          </div>

          {/* Application List - Updated to use React Table */}
          <div className="boardContent">
            <div className="boardThead table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    {table
                      .getHeaderGroups()
                      ?.map((headerGroup) =>
                        headerGroup.headers.map((header, i) => (
                          <th key={i}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </th>
                        ))
                      )}
                  </tr>
                </thead>
                <tbody>
                  {table.getRowModel()?.rows?.length > 0 ? (
                    table.getRowModel().rows.map((row) => (
                      <tr className={row.id} key={row.id}>
                        {row.getVisibleCells().map((cell, i) => (
                          <td key={i} className="">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={table.getHeaderGroups()?.[0]?.headers.length}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        <NotFound text="You have not filled any form yet" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={() => getTotalPages()}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
          />
        </div>
      </div>
    </div>
  );
};
export default AppDashboard;
